import { useTranslation } from 'react-i18next'
import { ArrowRight, CircleCheckBig, CircleDot } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { TicketStatusChip } from './TicketStatusChip'
import { formatAbsoluteDate, formatRelativeDate } from '../lib/timelineDate'
import { TimelineRow } from './TimelineRow'

interface TimelineStatus {
  code: string
  name: string
  color?: string | null
  bg?: string | null
  isFinal: boolean
}

interface TimelineStateChangeProps {
  /** Ausente en la primera transición: se pinta sin flecha ni chip de origen. */
  fromState?: TimelineStatus | null
  toState: TimelineStatus
  createdAt: string
  withRail?: boolean
}

/**
 * Hito de estado: línea compacta SIN burbuja — es lo que hace legible el
 * timeline frente a una lista uniforme de mensajes. Los chips son los del
 * nomenclador (`TicketStatusChip`), con su color y su nombre: el color nunca es
 * el único portador del significado, y el texto accesible nombra origen y
 * destino para quien no lo ve.
 */
export function TimelineStateChange({
  fromState,
  toState,
  createdAt,
  withRail = true,
}: TimelineStateChangeProps) {
  const { t, i18n } = useTranslation()
  const isFinal = toState.isFinal

  const accessibleText = fromState
    ? t('tickets.detail.stateChangedFrom', {
        from: fromState.name,
        to: toState.name,
        date: formatAbsoluteDate(createdAt),
      })
    : t('tickets.detail.stateChangedTo', {
        to: toState.name,
        date: formatAbsoluteDate(createdAt),
      })

  const marker = (
    <span
      className={cn(
        'grid size-8 place-items-center rounded-full ring-4 ring-white',
        isFinal
          ? 'bg-primary/15 text-primary ring-offset-0'
          : 'bg-neutral-100 text-ink-muted',
      )}
    >
      {isFinal ? (
        <CircleCheckBig className="size-4" />
      ) : (
        <CircleDot className="size-4" />
      )}
    </span>
  )

  return (
    <TimelineRow marker={marker} withRail={withRail}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted',
          // El desenlace del ticket es lo que el cliente busca al abrirlo: se
          // destaca en vez de quedar como una línea más.
          isFinal && 'rounded-lg bg-primary/[0.06] px-2.5 py-1.5 ring-1 ring-primary/15',
        )}
      >
        <span className="sr-only">{accessibleText}</span>
        <span aria-hidden="true" className="flex flex-wrap items-center gap-2">
          {fromState && (
            <>
              <TicketStatusChip status={fromState} />
              <ArrowRight className="size-3.5 shrink-0 text-ink-muted" />
            </>
          )}
          <TicketStatusChip status={toState} />
        </span>
        <time
          aria-hidden="true"
          dateTime={createdAt}
          title={formatAbsoluteDate(createdAt)}
        >
          {formatRelativeDate(createdAt, i18n.language)}
        </time>
      </div>
    </TimelineRow>
  )
}
